import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff", site_id: "", sites: [] });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => { api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    load();
    api.get("/sites").then((r) => setSites(r.data)).catch(() => {});
  }, [load]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleSite = (sid) => setForm((f) => ({ ...f, sites: f.sites.includes(sid) ? f.sites.filter((x) => x !== sid) : [...f.sites, sid] }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.role === "staff" && !form.site_id) return toast.error("Pilih website untuk staff");
    setBusy(true);
    try {
      const payload = { name: form.name, email: form.email, password: form.password, role: form.role };
      if (form.role === "staff") payload.site_id = form.site_id;
      if (form.role === "trainer") payload.sites = form.sites;
      await api.post("/users", payload);
      toast.success(`Akun ${form.role} dibuat`);
      setForm({ name: "", email: "", password: "", role: "staff", site_id: "", sites: [] });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const resetPw = (u) => { const p = window.prompt(`Password baru untuk ${u.email}:`); if (p) act(() => api.post(`/users/${u.id}/reset-password`, { password: p }), "Password direset"); };
  const toggleDisable = (u) => act(() => api.post(`/users/${u.id}/${u.disabled ? "enable" : "disable"}`), u.disabled ? "Diaktifkan" : "Dinonaktifkan");
  const del = (u) => { if (window.confirm(`Hapus ${u.email}?`)) act(() => api.delete(`/users/${u.id}`), "Dihapus"); };
  const assign = (u) => { const sid = window.prompt(`Assign website (site_id) untuk ${u.email}. Pilihan: ${sites.map((s) => s.site_id).join(", ")}`); if (sid) act(() => api.post(`/users/${u.id}/sites`, { site_id: sid }), "Website di-assign"); };
  const unassign = (u, sid) => act(() => api.delete(`/users/${u.id}/sites/${sid}`), "Akses website dicabut");

  return (
    <div data-testid="user-management-page">
      <div className="section-head">User Management</div>

      <form onSubmit={submit} style={{ maxWidth: 560, marginBottom: 20 }} data-testid="user-form">
        <div className="form-row"><label>Nama</label>
          <input className="form-input" value={form.name} onChange={(e) => setF("name", e.target.value)} data-testid="um-name" required /></div>
        <div className="form-row"><label>Email / Username</label>
          <input className="form-input" type="email" value={form.email} onChange={(e) => setF("email", e.target.value)} data-testid="um-email" required /></div>
        <div className="form-row"><label>Password</label>
          <input className="form-input" value={form.password} onChange={(e) => setF("password", e.target.value)} data-testid="um-password" required /></div>
        <div className="form-row"><label>Role</label>
          <select className="form-select" value={form.role} onChange={(e) => setF("role", e.target.value)} data-testid="um-role">
            <option value="staff">STAFF</option><option value="trainer">TRAINER</option>
          </select></div>
        {form.role === "staff" && (
          <div className="form-row"><label>Website (Site)</label>
            <select className="form-select" value={form.site_id} onChange={(e) => setF("site_id", e.target.value)} data-testid="um-site">
              <option value="">-- pilih website --</option>
              {sites.map((s) => <option key={s.site_id} value={s.site_id}>{s.display_name} ({s.site_id})</option>)}
            </select></div>
        )}
        {form.role === "trainer" && (
          <div className="form-row"><label>Akses Website</label>
            <div>{sites.map((s) => (
              <label key={s.site_id} style={{ marginRight: 12, fontWeight: "normal" }}>
                <input type="checkbox" checked={form.sites.includes(s.site_id)} onChange={() => toggleSite(s.site_id)} data-testid={`um-siteopt-${s.site_id}`} /> {s.display_name}
              </label>
            ))}</div></div>
        )}
        <div className="form-row"><label></label>
          <button className="btn btn-primary" disabled={busy} data-testid="um-submit">+ Buat Akun</button></div>
      </form>

      <div className="section-head">Daftar User</div>
      <table className="panel-table" data-testid="users-table">
        <thead><tr><th>Nama</th><th>Email</th><th>Role</th><th>Website</th><th>Status</th><th>Aksi</th></tr></thead>
        <tbody>
          {users.length === 0 && <tr className="empty-row"><td colSpan={6}>Belum ada user</td></tr>}
          {users.map((u) => (
            <tr key={u.id} data-testid={`um-row-${u.email}`}>
              <td>{u.name}</td>
              <td>{u.email}</td>
              <td>{u.role.toUpperCase()}</td>
              <td>
                {u.role === "staff" && (u.site_id || "-")}
                {u.role === "trainer" && (u.sites || []).map((s) => (
                  <span key={s} style={{ marginRight: 6 }}>{s} <a className="link-blue" onClick={() => unassign(u, s)}>×</a></span>
                ))}
                {u.role === "super_admin" && "ALL"}
              </td>
              <td>{u.disabled ? <span className="badge badge-fail">OFF</span> : <span className="badge badge-pass">ON</span>}</td>
              <td className="nowrap" style={{ fontSize: 11 }}>
                <a className="link-blue" onClick={() => resetPw(u)} data-testid={`reset-${u.email}`}>Reset PW</a>{" | "}
                <a className="link-blue" onClick={() => toggleDisable(u)} data-testid={`toggle-${u.email}`}>{u.disabled ? "Enable" : "Disable"}</a>
                {u.role === "trainer" && <>{" | "}<a className="link-blue" onClick={() => assign(u)} data-testid={`assign-${u.email}`}>Assign</a></>}
                {" | "}<a className="link-blue" onClick={() => del(u)} data-testid={`del-${u.email}`}>Hapus</a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
